import { faTrophy } from '@fortawesome/pro-regular-svg-icons';
import {
  faCircle,
  faPlusCircle,
  faTrophy as faTrophySolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import VoxelAlert from '../../../../../voxel/Alert/VoxelAlert';
import VoxelDialogActionSubmit from '../../../../../voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelLink from '../../../../../voxel/Link/VoxelLink';
import { VoxelPopover } from '../../../../../voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../../../voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../../../voxel/Popover/VoxelPopoverTrigger';
import { projectsApiService } from '../../../../../../graphql/projects/projectsApiService';
import { useRankCapacityCheck } from '../../../../../../logic/otherUtilities/useRankCapacityCheck';
import { ProjectKeywordGq } from '../../../../../../graphql/projects/models/ProjectKeywordGq';
import { ProjectGq } from '../../../../../../graphql/projects/models/ProjectGq';
import { CurrentUserGq } from '../../../../../../graphql/users/models/CurrentUserGq';
import GetRankDataDialog from '../../../../../voxel/Dialogs/GetRankDataDialog';
import VoxelText from '../../../../../voxel/Text/VoxelText';

export interface AddRankDataButtonProps {
  keyword: ProjectKeywordGq;
  project: ProjectGq;
  reload: () => Promise<unknown>;
  user: CurrentUserGq;
  setLoading: (loading: boolean) => void;
}

export const AddRankDataButton: React.FC<AddRankDataButtonProps> = ({
  keyword,
  project,
  reload,
  user,
  setLoading,
}) => {
  // Popover
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);

  // Add one keyword rank data
  const collectSerpDataMutation = projectsApiService.mutations.serp.useCollectSerpData();
  const handleCollectSerpData = () => {
    setLoading(true);
    collectSerpDataMutation({
      projectId: project.id,
      keywordQuery: {
        filtering: { projectId: project.id },
        selecting: {
          keywordIds: [keyword.id],
          invertSelection: false,
          emptyIsAll: false,
        },
      },
      newRanksSelected: true,
      updateRanksSelected: false,
    })
      .then(() => reload())
      .then(() => setLoading(false))
      .finally(() => setPopoverOpen(false));
  };

  // Add all keywords rank data
  const [dialogOpen, setDialogOpen] = useState(false);
  const selectedKeywordStats = projectsApiService.queries.useGetSelectedKeywordsStats(
    {
      projectId: project.id,
      keywordQuery: {
        filtering: { projectId: project.id },
      },
    },
    { skip: !popoverOpen }
  );

  const rankCapacity = useRankCapacityCheck(user?.team, 1);
  return (
    <>
      {dialogOpen && (
        <GetRankDataDialog
          project={project}
          open={dialogOpen}
          setOpen={setDialogOpen}
          filterState={{ projectId: project.id }}
          selectionState={undefined}
          resetSelection={undefined}
          showProjectDomain={undefined}
          selectedKeywordStats={selectedKeywordStats?.result}
          reload={reload}
          user={user}
        />
      )}
      <VoxelPopover open={popoverOpen} onOpenChange={setPopoverOpen} enableArrow placement="bottom">
        <VoxelPopoverTrigger>
          <button
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            className="group w-8 p-0.5 rounded-[2px]
                  outline outline-1 outline-offset-1 outline-voxel-gray-200 hover:outline-voxel-gray-500 
                   hover:bg-voxel-gray-100 
                  flex flex-row justify-center items-center"
          >
            <span className="fa-layers fa-fw mr-0.5">
              <FontAwesomeIcon icon={faTrophySolid} className={`text-voxel-gray-50`} />
              <FontAwesomeIcon
                icon={faTrophy}
                className={`text-voxel-gray-300 group-hover:text-voxel-gray-600`}
              />
              <FontAwesomeIcon
                icon={faCircle}
                size={'xs'}
                className={`text-white`}
                transform="right-10"
              />
              <FontAwesomeIcon
                icon={faPlusCircle}
                size={'xs'}
                className={`text-gray-300 group-hover:text-success-600/70`}
                transform="right-10"
              />
            </span>
          </button>
        </VoxelPopoverTrigger>
        <VoxelPopoverContent>
          <div className="flex items-center flex-row pl-4 pr-1 py-2 gap-1">
            <VoxelText>Add rank data on</VoxelText>
            <div className="p-3 flex flex-col gap-2">
              <VoxelDialogActionSubmit
                fullWidth
                size="md"
                title={'Keyword'}
                onClick={handleCollectSerpData}
              />
              <VoxelDialogActionSubmit
                fullWidth
                size="md"
                title={'All keywords'}
                onClick={() => {
                  setDialogOpen(true);
                  setPopoverOpen(false);
                }}
              />
            </div>
          </div>
          {user?.team && !rankCapacity?.rankSelectionOk && (
            <VoxelAlert severity="error" size="sm" iconSize="lg" roundedClass="rounded-t-none">
              Not enough left of your teams capacity. Go to{' '}
              <VoxelLink href="/team/usage" openInNewTab openInNewIcon>
                App usage
              </VoxelLink>{' '}
              for more information
            </VoxelAlert>
          )}
        </VoxelPopoverContent>
      </VoxelPopover>
    </>
  );
};

export default AddRankDataButton;
